// Library methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import VisualAcuityBar from "../VisualAcuityBar/VisualAcuityBar";
import VisualAcuityHeader from "../VisualAcuityHeader/VisualAcuityHeader";
import VisualAcuityScale from "../VisualAcuityScale/VisualAcuityScale";
import VisualAcuityIndex from "../VisualAcuityIndex/VisualAcuityIndex";

// Types
import {
  ExamSectionReportType,
  VisualAcuityChartRowType,
} from "src/services/exams/types";

// Styles
import styles from "../visualAcuity.module.css";

type TumblingEGraphType = {
  examData: ExamSectionReportType;
  isLiveUpdate?: boolean;
};

const TumblingEGraph = ({
  examData,
  isLiveUpdate = false,
}: TumblingEGraphType) => {
  const { t } = useTranslation();

  const lettersMap = useMemo(
    () => ({
      UP: "d",
      DOWN: "b",
      LEFT: "c",
      RIGHT: "a",
    }),
    []
  );

  const getChartLetters = useCallback(
    (chartRows: Array<VisualAcuityChartRowType>) => {
      let displayedLetters: Array<
        Array<{
          color: string;
          backgroundImage: string;
          backgroundSize: string;
          value: string;
        }>
      > = [];

      chartRows?.forEach((chartRow) => {
        displayedLetters.push(
          chartRow?.letters?.map((char) => ({
            color:
              char?.State === "GuessedCorrectly"
                ? "#13A321"
                : char?.State === "Skipped"
                  ? "#202338"
                  : char?.State === "None"
                    ? "#202338"
                    : "#B21515",
            backgroundImage:
              char?.State === "Skipped"
                ? "linear-gradient(0deg, #E7C43C60 25%, #FFF 25%, #FFF 50%, #E7C43C60 50%, #E7C43C60 75%, #FFF 75%, #FFF 100%)"
                : "none",
            backgroundSize: "8px 8px",
            value: lettersMap
              ? lettersMap[char?.Value as keyof typeof lettersMap]
              : char?.Value,
          }))
        );
      });

      if (
        examData?.state?.letterIndex &&
        examData?.state?.letterIndex >= 0 &&
        examData?.state?.rowIndex &&
        examData?.state?.rowIndex >= 0 &&
        displayedLetters[examData.state.rowIndex][examData.state.letterIndex]
      ) {
        displayedLetters[examData.state.rowIndex][examData.state.letterIndex][
          "color"
        ] = "#E2772E";
      }

      return displayedLetters;
    },
    [examData?.state?.rowIndex, examData?.state?.letterIndex, lettersMap]
  );

  const isRowWithScore = useCallback(
    (currentRowScaleFeet: string) => {
      if (isLiveUpdate) {
        return false;
      }

      if (examData?.postProcessing?.processedCharts?.[0]?.result === "0") {
        return currentRowScaleFeet === "20/20";
      }

      return (
        examData?.postProcessing?.processedCharts?.[0]?.result ===
        currentRowScaleFeet
      );
    },
    [isLiveUpdate, examData?.postProcessing?.processedCharts]
  );

  const isError = useMemo(
    () => !examData?.state?.charts || !examData?.state?.charts?.length,
    [examData?.state?.charts]
  );

  return (
    <Grid container item xs={12} key={examData?.exam.algorithm}>
      {!isError && <VisualAcuityHeader isLiveUpdate={isLiveUpdate} />}

      {isError ? (
        <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
          {t("Error: No patient responses recorded.")}
        </Typography>
      ) : (
        examData?.state?.charts?.map((chart) => {
          const chartRows = chart?.rows ?? [];
          const chartLetters = getChartLetters(chartRows);

          return (
            <Grid container item xs={12} key={chart?.index}>
              {chartLetters?.map((chartLetter, index) => (
                <Grid
                  container
                  item
                  key={index}
                  sx={{
                    border: isRowWithScore(
                      chartRows?.[index]?.snellenVisualAcuityScaleFeet
                    )
                      ? "3px solid #000"
                      : "3px solid #FFF",
                    borderRadius: "4px",
                  }}
                >
                  {/* Snellen Equivalent values */}
                  <VisualAcuityScale
                    chartRow={chartRows?.[index]}
                    isLiveUpdate={isLiveUpdate}
                  />

                  <Grid container item xs={6}>
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="center"
                      position={
                        index === 5 || index === 8 ? "relative" : "static"
                      }
                    >
                      {chartLetter?.map((letter, j: number) => {
                        const value = letter?.value;
                        return (
                          <Grid
                            item
                            key={j}
                            sx={{
                              backgroundImage: letter?.backgroundImage,
                              backgroundSize: letter?.backgroundSize,
                              display: "flex",
                              justifyContent: "center",
                              // Smaller letter boxes on narrow screens in
                              // the live update view, freeing up room for
                              // the scale/index columns beside them.
                              height: isLiveUpdate
                                ? { xs: "24px", sm: "32px" }
                                : "32px",
                              width: isLiveUpdate
                                ? { xs: "24px", sm: "32px" }
                                : "32px",
                            }}
                          >
                            <Typography
                              display="inline-block"
                              fontSize={
                                isLiveUpdate
                                  ? { xs: "18px", sm: "24px" }
                                  : "24px"
                              }
                              color={letter?.color}
                              className={styles.eye_chart_font}
                            >
                              {value}
                            </Typography>
                          </Grid>
                        );
                      })}
                      {(index === 5 || index === 8) && (
                        <VisualAcuityBar
                          index={index}
                          isLiveUpdate={isLiveUpdate}
                        />
                      )}
                    </Grid>
                  </Grid>

                  {/* LogMAR and Index values */}
                  <VisualAcuityIndex
                    chartRow={chartRows?.[index]}
                    index={index}
                    isLiveUpdate={isLiveUpdate}
                  />
                </Grid>
              ))}
            </Grid>
          );
        })
      )}
    </Grid>
  );
};

export default TumblingEGraph;
